import { Box, Stack, Typography } from '@mui/material';
import { Door, Location, VisitorGroup } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { getVisitorGroupsThatHaveLockAssignedDirectly } from 'utils/VisitorGroup/VisitorGroup';
import DrawerTemplate from 'v2/components/Drawer/DrawerTemplate';
import useDrawerContext from 'v2/components/Drawer/useDrawerContext';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import GroupCheckboxList from '../../components/GroupCheckboxList';

export interface EditDeviceGroupFormValues {
  selectedDeviceGroupIds: string[];
}

export const FORM_ID = 'edit-device-group-drawer-form';

type AssignDoorToDoorGroupOrVisitorGroup = (
  door: Door,
  doorGroupIds: string[],
  visitorGroupIds: string[]
) => Promise<{ success: boolean; errorMessage?: string }>;

interface Props {
  device: Door;
  locations: Location[];
  visitorGroups: VisitorGroup[];
  assignDoorToDoorGroupOrVisitorGroup: AssignDoorToDoorGroupOrVisitorGroup;
}

// Only rendered once device and locations have loaded, so the form is initialized with the
// real, current selection as its defaultValues - a later background refetch of this data won't
// touch an already-mounted form's state, so unsaved changes are never silently reset.
const EditDeviceGroupsForm = ({ device, locations, visitorGroups, assignDoorToDoorGroupOrVisitorGroup }: Props) => {
  const { translation } = useTranslation();
  const { closeDrawer } = useDrawerContext();
  const { showSnackbar } = useSnackbarContext();
  const [error, setError] = useState<string>();

  const {
    handleSubmit,
    control,
    formState: { isSubmitting }
  } = useForm<EditDeviceGroupFormValues>({
    mode: 'onTouched',
    defaultValues: {
      selectedDeviceGroupIds: device.locations.map((location) => location.id)
    }
  });

  const onSubmit = async (data: EditDeviceGroupFormValues) => {
    setError(undefined);
    // assignDoorToDoorGroupOrVisitorGroup always sets both device groups and user groups in one
    // call. This drawer only edits device groups, so we re-send the current user groups unchanged.
    const currentUserGroupIds = getVisitorGroupsThatHaveLockAssignedDirectly(visitorGroups, device.id).map(
      (visitorGroup) => visitorGroup.id
    );
    const result = await assignDoorToDoorGroupOrVisitorGroup(device, data.selectedDeviceGroupIds, currentUserGroupIds);
    if (result.success) {
      showSnackbar(translation.saved_changes, { severity: 'success' });
      closeDrawer();
    } else {
      setError(result.errorMessage ?? translation.error_while_updating_device);
    }
  };

  return (
    <DrawerTemplate
      id="edit-device-group-drawer"
      loading={isSubmitting}
      title={translation.edit_device_groups}
      primaryAction={{
        label: translation.save,
        onClick: handleSubmit(onSubmit),
        loading: isSubmitting,
        form: FORM_ID,
        id: 'save-button'
      }}
      secondaryAction={{ label: translation.cancel, id: 'cancel-button' }}
      error={error}
      onDismissError={() => setError(undefined)}
    >
      <Stack
        id={FORM_ID}
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        gap={3}
        sx={{
          height: '100%',
          minHeight: 0
        }}
      >
        <Typography variant="body2" color="textSecondary">
          {translation.select_device_group_membership.replace('{{name}}', device.name)}
        </Typography>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <GroupCheckboxList
            items={locations}
            name="selectedDeviceGroupIds"
            control={control}
            searchPlaceholder={translation.search_device_group}
          />
        </Box>
      </Stack>
    </DrawerTemplate>
  );
};

export default EditDeviceGroupsForm;
